/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { ExcelService } from '@app/core/infrastructure/storage';
import { ExportColumn } from '@app/shared/types/ui';
import type { Worksheet } from 'exceljs';

@Injectable({ providedIn: 'root' })
export class TableExportService {
  private readonly excelService = inject(ExcelService);

  async exportToExcel<T extends Record<string, any>>(
    data: T[],
    columns: ExportColumn[],
    fileName: string,
    sheetName: string,
    title?: string
  ): Promise<void> {
    const workbook = await this.excelService.createWorkbook();
    const worksheet = this.excelService.createWorksheet(workbook, sheetName || 'Datos');
    if (title) {
      this.addTitleRow(worksheet, title, columns.length);
    }

    const headers = columns.map(col => col.header);
    this.excelService.addHeaders(worksheet, headers);
    const rows = data.map(item =>
      columns.map(col => {
        const value = this.getNestedProperty(item, col.key);
        return this.formatValue(value, col.format);
      })
    );
    this.excelService.addData(worksheet, rows);
    this.applyCellStyles(worksheet, columns);
    this.addSummaryRow(worksheet, columns, data);
    this.excelService.autoFitColumns(worksheet);
    await this.excelService.generateExcelFile(workbook, fileName);
  }

  private addTitleRow(worksheet: Worksheet, title: string, columnCount: number): void {
    const titleRow = worksheet.addRow([title]);
    worksheet.mergeCells(titleRow.number, 1, titleRow.number, columnCount);
    titleRow.height = 30;
    const titleCell = titleRow.getCell(1);
    titleCell.style = {
      font: { bold: true, size: 16, color: { argb: 'FF0657A8' } },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };
    worksheet.addRow([]);
    worksheet.views = [{ state: 'frozen', ySplit: 3 }];
  }

  private applyCellStyles(worksheet: Worksheet, columns: ExportColumn[]): void {
    const dataStartRow = worksheet.getRow(1).getCell(1).value ? 4 : 2;

    for (let i = dataStartRow; i <= worksheet.rowCount; i++) {
      const row = worksheet.getRow(i);
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const columnConfig = columns[colNumber - 1];
        if (!columnConfig) return;

        cell.alignment = {
          vertical: 'middle',
          horizontal: columnConfig.alignment || 'left'
        };

        switch (columnConfig.format) {
          case 'currency':
            cell.numFmt = '$#,##0.00;[Red]-$#,##0.00';
            cell.alignment.horizontal = 'right';
            break;
          case 'percentage':
            cell.numFmt = '0.00%';
            break;
          case 'date':
            cell.numFmt = 'dd/mm/yyyy';
            cell.alignment.horizontal = 'center';
            break;
        }

        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
        };
      });
    }
  }

  private addSummaryRow(worksheet: Worksheet, columns: ExportColumn[], data: any[]): void {
    const summaryRowData: (string | number | { formula: string; } | null)[] = [];
    let hasSummary = false;

    columns.forEach((col, index) => {
      if (col.format === 'currency') {
        hasSummary = true;
        const colLetter = String.fromCharCode(65 + index);
        const startRow = worksheet.getCell('A1').value ? 4 : 2;
        const endRow = startRow + data.length - 1;
        summaryRowData.push({ formula: `SUM(${colLetter}${startRow}:${colLetter}${endRow})` });
      } else {
        summaryRowData.push(null);
      }
    });

    if (!hasSummary) return;

    summaryRowData[0] = 'Totales';

    const summaryRow = worksheet.addRow(summaryRowData);
    summaryRow.eachCell(cell => {
      cell.style = {
        font: { bold: true, size: 12 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDDDDD' } },
        border: { top: { style: 'medium' } }
      };

      if (cell.formula) {
        cell.numFmt = '$#,##0.00;[Red]-$#,##0.00';
      }
    });
  }

  private formatValue(value: unknown, format?: string): string | number | Date {
    if (value === null || value === undefined) {
      return '';
    }

    switch (format) {
      case 'currency':
        return typeof value === 'number' ? value : Number(value) || 0;
      case 'percentage':
        return typeof value === 'number' ? value / 100 : Number(value) / 100 || 0;
      case 'date':
        return value instanceof Date ? value : new Date(String(value));
      default:
        return String(value);
    }
  }

  private getNestedProperty(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((prev: any, curr: string) => {
      return prev ? prev[curr] : '';
    }, obj);
  }
}
